// src/utils/qbTotalMismatch.js — qbpushcheck-7e2c5a94 (BACKLOG row 31, seam T1)
// Reads the marker the QuickBooks push leaves on an invoice when QuickBooks ended up holding a
// different total or tax than the customer was billed (functions/utils/qbPushCheck.js writes
// invoice.qbTotalMismatch = { total, tax, qbTotal, qbTax, checkedAt }; a matching push clears it).
// Display only: the invoice page shows a warning banner and the invoices list an amber badge,
// beside the other QuickBooks sync warnings. Nothing here changes an invoice.

const isMoney = (v) => typeof v === 'number' && Number.isFinite(v);

/** The mismatch recorded on this invoice, or null (no marker, a malformed one, or not in QuickBooks). */
function qbTotalMismatchOf(invoice) {
    const m = invoice && invoice.qbTotalMismatch;
    if (!invoice || !invoice.qbInvoiceId || !m || typeof m !== 'object') return null;
    if (![m.total, m.tax, m.qbTotal, m.qbTax].every(isMoney)) return null;
    return { total: m.total, tax: m.tax, qbTotal: m.qbTotal, qbTax: m.qbTax, checkedAt: typeof m.checkedAt === 'string' ? m.checkedAt : null };
}

/** The banner's words. formatMoney: (number) => "$410.00". */
function qbTotalMismatchText(mismatch, formatMoney) {
    return {
        headline: 'QuickBooks has a different amount for this invoice.',
        spatchy: `Spatchy (what the customer was billed): tax ${formatMoney(mismatch.tax)}, total ${formatMoney(mismatch.total)}.`,
        quickbooks: `QuickBooks: tax ${formatMoney(mismatch.qbTax)}, total ${formatMoney(mismatch.qbTotal)}.`,
        advice: 'QuickBooks worked out its own tax from the tax codes on the lines. Open this invoice in QuickBooks and correct it so the two agree.',
        badge: 'Synced · amount differs',
        badgeTitle: `QuickBooks has tax ${formatMoney(mismatch.qbTax)}, total ${formatMoney(mismatch.qbTotal)}; Spatchy billed tax ${formatMoney(mismatch.tax)}, total ${formatMoney(mismatch.total)}.`,
    };
}

// qbrecheck-3a7d9e52 -- "Check again": the invoice page receives its invoice as a prop, so the
// server's answer is laid over it until the page is given a fresh copy.
// answer = what recheckQuickBooksInvoiceTotal returned ({ matches, total, tax, qbTotal, qbTax,
// checkedAt }), or null/undefined when the button has not been used on this invoice.
function withRecheckAnswer(invoice, answer) {
    if (!invoice || !answer || typeof answer !== 'object') return invoice;
    if (answer.matches === true) return { ...invoice, qbTotalMismatch: null };
    if (answer.matches !== false || ![answer.total, answer.tax, answer.qbTotal, answer.qbTax].every(isMoney)) return invoice; // an answer without figures changes nothing
    return { ...invoice, qbTotalMismatch: { total: answer.total, tax: answer.tax, qbTotal: answer.qbTotal, qbTax: answer.qbTax, checkedAt: answer.checkedAt } };
}

/** The button's words, and what to tell the user after a check. */
const QB_RECHECK_WORDS = {
    button: 'Check again',
    busy: 'Checking QuickBooks…',
    hint: 'Corrected it in QuickBooks? Check again to clear this warning. This only reads from QuickBooks; it changes nothing there.',
    nowMatches: 'QuickBooks now has the same tax and total as this invoice. The warning has been cleared.',
    stillDiffers: 'QuickBooks still has a different amount. The figures in the warning are now up to date.',
    failed: 'Could not check QuickBooks. Nothing was changed.',
};

export { qbTotalMismatchOf, qbTotalMismatchText, withRecheckAnswer, QB_RECHECK_WORDS };
